import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { UserVoteListAggregate } from "../../domain/vote/Models/UserVoteListAggregate"
import { DomainEventDetail, DomainEvents } from "../../domain/_kernel/Events"
import { TransactionType } from "../../domain/ethereum/Models/Transaction"
import { isReviewerSession } from "../../utils/reviewer"

interface UserVoteListState {
  userVoteListAggregate?: UserVoteListAggregate
  isLoading: boolean
  error?: string
  _isListening: boolean
}

interface UserVoteListActions {
  fetchUserVoteList: () => Promise<UserVoteListAggregate | undefined>
  reset: () => void
  startListening: () => void
  stopListening: () => void
}

type UserVoteListStore = UserVoteListState & UserVoteListActions

export const useUserVoteListStore = create<UserVoteListStore>()(
  devtools(
    (set, get) => {
      const handleSuccessTransaction = (e: CustomEvent<DomainEventDetail[DomainEvents.SUCCESS_TRANSACTION]>) => {
        if (
          e.detail.transaction.type === TransactionType.VOTE ||
          e.detail.transaction.type === TransactionType.VOTE_EDIT
        ) {
          get().fetchUserVoteList()
        }
      }

      return {
        userVoteListAggregate: undefined,
        isLoading: false,
        error: undefined,
        _isListening: false,

        fetchUserVoteList: async () => {
          const state = get()
          if (state.isLoading || isReviewerSession()) return undefined // server denies getUserVoteList for reviewers

          set({ isLoading: true, error: undefined })

          try {
            const { userVoteListAggregate } = await window.domain.GetUserVoteListAggregateUseCase.execute()
            set({ userVoteListAggregate, isLoading: false })
            return userVoteListAggregate
          } catch (error) {
            set({ error: (error as Error).message, isLoading: false })
            throw error
          }
        },

        reset: () => {
          set({ userVoteListAggregate: undefined, isLoading: false, error: undefined })
        },

        startListening: () => {
          if (get()._isListening) return
          set({ _isListening: true })
          window.addEventListener(DomainEvents.SUCCESS_TRANSACTION, handleSuccessTransaction as EventListener)
        },

        stopListening: () => {
          if (!get()._isListening) return
          set({ _isListening: false })
          window.removeEventListener(DomainEvents.SUCCESS_TRANSACTION, handleSuccessTransaction as EventListener)
        },
      }
    },
    { name: "UserVoteListStore" },
  ),
)
